import InputField from "components/common/Input/Input";
import SelectOption from "components/common/Select/Select";
import SwitchWithIcon from "components/common/SwitchWithIcon";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useWatch } from "react-hook-form";
import { CgRemoveR } from "react-icons/cg";
import {
  buildShiftNdisItemsUrl,
  fetchShiftNdisItems,
  mapShiftNdisItemsToOptions,
} from "Utils/shiftNdisItemsCache";
import { getLineItemLabel } from "Constants/constant";
import {
  capitalizeFirstLetter,
  getShiftLength,
  parseBooleanVal,
} from "Utils/utils";
import {
  fundingTypeFromInvoiceServiceTypeName,
  getShiftScreenFundingTypes,
  groupServiceTypesByFundingType,
} from "Utils/rosteringFundingType";
import {
  ADVANCE_CLAIM_TYPE_OPTIONS,
  DEFAULT_ADVANCE_CLAIM_TYPE,
  claimTypeFromIsTravel,
  isTravelFromClaimType,
  mapNdisOptionsForClaimType,
  showClaimTypeInScheduleTimesheets,
  toTimeInputValue,
} from "./advanceModeSplitDefaults";
import {
  ADVANCE_TIME_INPUT_CLASS,
  AdvanceModeFieldError,
  AdvanceModeFieldLabel,
} from "./advanceModeUi";
import {
  getBasicShiftWindowMinutes,
  isBasicSameClockTimeFullDay,
  validateBasicSplitEndTime,
} from "./basicModeValidation";
import {
  buildCustomLineItemsSelectOptionsUrl,
  fetchCustomLineItemsSelectOptions,
} from "Utils/customLineItemsSelectOptionsCache";
import TimePicker from "react-time-picker-input";
const FUNDING_TYPE_LABELS = {
  NDIS: "NDIS",
  AGED_CARE: "Aged Care",
  PRIVATE: "Private",
  OTHER: "Other",
};
const getParticipantId = (selectedParticipant) => {
  const participant = Array.isArray(selectedParticipant)
    ? selectedParticipant[0]
    : selectedParticipant;
  return participant?.id ?? participant?.participant_id ?? null;
};
const SplitShift = ({
  shift,
  setValue,
  register,
  onRemove,
  control,
  serviceTypes,
  selectedParticipant,
  index,
  clearErrors,
  errors,
  watch,
  totalShiftDuration,
  setIsLessThan12Hours,
  spRosteringSetting,
  onSplitSleepoverChange,
  useNativeTimeInput = false,
  showServiceCategoryError = false,
}) => {
  const splitShiftValues = useWatch({
    control,
    name: `splitShifts.${index}`,
  });
  const allSplitShifts = useWatch({ control, name: "splitShifts" }) || [];
  const shiftDate = useWatch({ control, name: "shift_date" });
  const splitInvoiceServiceTypeId = useWatch({
    control,
    name: `splitShifts.${index}.invoice_service_type_id`,
  });
  const splitShift = {
    ...shift,
    ...splitShiftValues,
  };
  const isFirstSplitShift = index === 0;
  const isLastSplitShift = index === allSplitShifts.length - 1;
  let nextDayStartIndex = -1;
  for (let i = 0; i < allSplitShifts.length; i++) {
    const s = allSplitShifts[i];
    if (s?.start_time && s?.end_time && moment(s.end_time, "HH:mm").isBefore(moment(s.start_time, "HH:mm"))) {
      nextDayStartIndex = i;
      break;
    }
  }
  const participantId = getParticipantId(selectedParticipant);
  const selectedServiceCategory = useMemo(() => {
    if (splitInvoiceServiceTypeId == null || splitInvoiceServiceTypeId === "") {
      return null;
    }
    const found =
      serviceTypes?.find(
        (item) => String(item?.value) === String(splitInvoiceServiceTypeId)
      ) || null;
    if (found) return found;
    const orphanLabel =
      splitShift?.service_type_name || splitShift?.custom_item_service_category;
    // Only preserve out-of-list categories when editing a saved shift (real label).
    // Bare IDs on new shifts (e.g. participant default NDIS category under Aged Care-only SP)
    // are cleared by the effect below — do not show "Previously selected category".
    if (!orphanLabel) {
      return null;
    }
    return {
      value: splitInvoiceServiceTypeId,
      label: orphanLabel,
      funding_type:
        splitShift?.service_type_funding_type ||
        splitShift?.custom_item_funding_type ||
        splitShift?.custom_line_item?.funding_type ||
        undefined,
      __orphan: true,
    };
  }, [
    serviceTypes,
    splitInvoiceServiceTypeId,
    splitShift?.service_type_name,
    splitShift?.service_type_funding_type,
    splitShift?.custom_item_service_category,
    splitShift?.custom_item_funding_type,
    splitShift?.custom_line_item?.funding_type,
  ]);
  const groupedServiceTypes = useMemo(() => {
    const types = [...(serviceTypes || [])];
    if (
      selectedServiceCategory?.__orphan &&
      !types.some(
        (item) => String(item?.value) === String(selectedServiceCategory.value)
      )
    ) {
      types.push(selectedServiceCategory);
    }
    return groupServiceTypesByFundingType(types);
  }, [serviceTypes, selectedServiceCategory]);
  useEffect(() => {
    if (splitInvoiceServiceTypeId == null || splitInvoiceServiceTypeId === "") {
      return;
    }
    // Wait until service categories are loaded — clearing against an empty list
    // wipes create-flow defaults and blocks custom line-item fetch.
    if (!Array.isArray(serviceTypes) || serviceTypes.length === 0) {
      return;
    }
    const isInList = serviceTypes.some(
      (item) => String(item?.value) === String(splitInvoiceServiceTypeId)
    );
    if (isInList) {
      return;
    }
    const hasSavedCategoryLabel =
      splitShift?.service_type_name || splitShift?.custom_item_service_category;
    if (hasSavedCategoryLabel) {
      return;
    }
    setValue(`splitShifts.${index}.invoice_service_type_id`, "");
    setValue(`splitShifts.${index}.invoice_item_id`, "");
    setValue(`splitShifts.${index}.custom_line_item_uuid`, null);
    setValue(`splitShifts.${index}.support_item_number`, "");
    setValue(`splitShifts.${index}.support_item_name`, "");
    setValue(`splitShifts.${index}.price`, "");
  }, [
    splitInvoiceServiceTypeId,
    serviceTypes,
    splitShift?.service_type_name,
    splitShift?.custom_item_service_category,
    index,
    setValue,
  ]);

  useEffect(() => {
    if (
      splitInvoiceServiceTypeId != null &&
      splitInvoiceServiceTypeId !== ""
    ) {
      return;
    }
    const participantCategoryId = !Array.isArray(selectedParticipant)
      ? selectedParticipant?.invoice_category_id
      : null;
    if (!participantCategoryId) {
      return;
    }
    const isInList = serviceTypes?.some(
      (item) => String(item?.value) === String(participantCategoryId)
    );
    if (isInList) {
      setValue(
        `splitShifts.${index}.invoice_service_type_id`,
        participantCategoryId
      );
    }
  }, [
    splitInvoiceServiceTypeId,
    selectedParticipant,
    serviceTypes,
    index,
    setValue,
  ]);
  const [NDISItemNameOptions, setNDISItemName] = useState([]);
  const [customLineItemOptions, setCustomLineItemOptions] = useState([]);
  const [ndisLoading, setNdisLoading] = useState(false);
  const [customLoading, setCustomLoading] = useState(false);
  const resolvedFundingType = String(
    selectedServiceCategory?.funding_type ||
      fundingTypeFromInvoiceServiceTypeName(selectedServiceCategory?.label) ||
      ""
  ).toUpperCase();
  const isNdisFundingType = resolvedFundingType === "NDIS";
  const lineItemLabel = getLineItemLabel(spRosteringSetting?.care_sector) || "Support item";
  const shiftScreenFundingTypes = useMemo(
    () => getShiftScreenFundingTypes(spRosteringSetting),
    [spRosteringSetting?.care_sector, spRosteringSetting?.shift_screen_funding_types]
  );
  const shouldUseShiftFundingTypeSetting = shiftScreenFundingTypes.length > 1;
  const showClaimTypeForSplits = showClaimTypeInScheduleTimesheets(spRosteringSetting);
  const showTravelForSplits =
    showClaimTypeForSplits ||
    parseBooleanVal(
      spRosteringSetting?.show_travel_toggle_schedule_timesheets
    ) ||
    spRosteringSetting?.show_travel_toggle_schedule_timesheets === 1 ||
    spRosteringSetting?.show_travel_toggle_schedule_timesheets === "1";
  // Same as advance: claim_type is primary; is_travel is legacy fallback only.
  const claimTypeForSplit = useMemo(() => {
    if (splitShift?.claim_type) return splitShift.claim_type;
    if (splitShift?.is_travel != null) {
      return claimTypeFromIsTravel(splitShift.is_travel);
    }
    return showClaimTypeForSplits ? DEFAULT_ADVANCE_CLAIM_TYPE : null;
  }, [
    splitShift?.claim_type,
    splitShift?.is_travel,
    showClaimTypeForSplits,
  ]);
  const isTravelForSplit = isTravelFromClaimType(claimTypeForSplit);

  const selectedClaimType = useMemo(
    () =>
      claimTypeForSplit
        ? ADVANCE_CLAIM_TYPE_OPTIONS.find((o) => o.value === claimTypeForSplit) ||
          null
        : null,
    [claimTypeForSplit]
  );

  const NDISItemNameOptionsForSelect = useMemo(() => {
    if (!NDISItemNameOptions?.length) return NDISItemNameOptions;
    if (!showClaimTypeForSplits) return NDISItemNameOptions;
    return mapNdisOptionsForClaimType(NDISItemNameOptions, claimTypeForSplit);
  }, [NDISItemNameOptions, showClaimTypeForSplits, claimTypeForSplit]);

  const shiftScreenFundingTypesKey = shiftScreenFundingTypes.join(",");

  const savedCustomLineItemUuid = splitShift?.custom_line_item_uuid || null;
  const savedInvoiceItemId =
    splitShift?.invoice_item_id != null && splitShift?.invoice_item_id !== ""
      ? splitShift.invoice_item_id
      : null;

  /** Rebuild a dropdown option for a line item already saved on this split. */
  const buildOrphanCustomLineItemOption = () => {
    const uuid = savedCustomLineItemUuid;
    if (!uuid) return null;
    const cli = splitShift?.custom_line_item;
    const label =
      cli?.description ||
      splitShift?.support_item_name ||
      cli?.unique_code ||
      splitShift?.support_item_number ||
      "Saved line item";
    return {
      value: uuid,
      label,
      number: cli?.unique_code || splitShift?.support_item_number || "",
      name: cli?.description || splitShift?.support_item_name || label,
      price: cli?.rate ?? splitShift?.price ?? "",
      uuid,
      funding_type: cli?.funding_type,
      invoice_service_type_id:
        cli?.invoice_service_type_id ?? splitInvoiceServiceTypeId,
      __type: "custom",
      __orphan: true,
    };
  };

  const buildOrphanNdisLineItemOption = () => {
    if (!savedInvoiceItemId || savedCustomLineItemUuid) return null;
    const label =
      splitShift?.support_item_name ||
      splitShift?.support_item_number ||
      "Saved line item";
    return {
      value: savedInvoiceItemId,
      label,
      number: splitShift?.support_item_number || "",
      name: splitShift?.support_item_name || label,
      price: splitShift?.price ?? "",
      __type: "ndis",
      __orphan: true,
    };
  };

  useEffect(() => {
    const serviceTypeId =
      selectedServiceCategory?.value ?? splitInvoiceServiceTypeId;
    if (!serviceTypeId && !savedCustomLineItemUuid) {
      setCustomLineItemOptions([]);
      setCustomLoading(false);
      return;
    }

    const url = buildCustomLineItemsSelectOptionsUrl({
      participantId,
      fundingTypes: shiftScreenFundingTypes,
      filterByFundingType: shouldUseShiftFundingTypeSetting,
      includeUuid: savedCustomLineItemUuid,
      invoiceServiceTypeId: serviceTypeId || null,
    });

    let cancelled = false;
    // Drop previous category customs immediately (Aged Care / Private / Other).
    setCustomLineItemOptions([]);
    setCustomLoading(true);
    fetchCustomLineItemsSelectOptions(url)
      .then((payload) => {
        if (cancelled) return;
        const items = payload?.items ?? payload ?? [];
        const typeId =
          serviceTypeId != null && serviceTypeId !== ""
            ? String(serviceTypeId)
            : null;
        // Prefer server-scoped rows; keep client filter as a safety net.
        const scoped = (items || []).filter((item) => {
          if (!typeId) return true;
          const itemTypeId =
            item?.invoice_service_type_id ?? item?.service_category_id;
          return itemTypeId == null || String(itemTypeId) === typeId;
        });
        setCustomLineItemOptions(scoped);
      })
      .catch(() => {
        if (!cancelled) setCustomLineItemOptions([]);
      })
      .finally(() => {
        if (!cancelled) setCustomLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    selectedServiceCategory?.value,
    splitInvoiceServiceTypeId,
    participantId,
    shouldUseShiftFundingTypeSetting,
    shiftScreenFundingTypesKey,
    savedCustomLineItemUuid,
  ]);
  const groupedLineItemOptions = useMemo(() => {
    const catalogOptions = (NDISItemNameOptionsForSelect || []).map((item) => ({
      ...item,
      __type: item.is_custom ? "custom" : "ndis",
    }));
    const ndis = catalogOptions.filter((item) => item.__type !== "custom");
    const customFromCatalogue = catalogOptions.filter((item) => item.__type === "custom");
    let custom = (customLineItemOptions || [])
      .filter(
        (item) =>
          !shouldUseShiftFundingTypeSetting ||
          shiftScreenFundingTypes.includes(item.funding_type) ||
          (savedCustomLineItemUuid &&
            String(item.uuid) === String(savedCustomLineItemUuid))
      )
      .map((item) => ({
        value: item.uuid,
        label: [item.unique_code, item.description].filter(Boolean).join(" "),
        number: item.unique_code,
        name: [item.unique_code, item.description].filter(Boolean).join(" "),
        price: item.rate,
        uuid: item.uuid,
        funding_type: item.funding_type,
        invoice_service_type_id: item.invoice_service_type_id,
        __type: "custom",
      }));
    customFromCatalogue.forEach((item) => {
      const key = String(item.uuid || item.value || "");
      if (!key) return;
      if (custom.some((existing) => String(existing.uuid || existing.value) === key)) {
        return;
      }
      custom.push(item);
    });

    // Keep roster-saved selections visible only when this category's catalogue
    // is empty (true edit orphan). Never inject another category's line item
    // into Aged Care / Private / Other / NDIS lists that already have rows.
    const orphanCustom = buildOrphanCustomLineItemOption();
    const orphanCustomBelongsHere =
      orphanCustom &&
      (orphanCustom.invoice_service_type_id == null ||
        String(orphanCustom.invoice_service_type_id) ===
          String(splitInvoiceServiceTypeId));
    if (
      orphanCustom &&
      orphanCustomBelongsHere &&
      custom.length === 0 &&
      !custom.some((item) => String(item.uuid) === String(orphanCustom.uuid))
    ) {
      custom.unshift(orphanCustom);
    }

    // Mirror NDIS claim-type suffix (e.g. " - NF2F") on custom line items.
    if (showClaimTypeForSplits && claimTypeForSplit && custom.length) {
      custom = mapNdisOptionsForClaimType(custom, claimTypeForSplit);
    }
    const orphanNdis = buildOrphanNdisLineItemOption();
    if (
      orphanNdis &&
      ndis.length === 0 &&
      !ndis.some((item) => String(item.value) === String(orphanNdis.value))
    ) {
      ndis.unshift(orphanNdis);
    }

    const groups = [];
    if (ndis.length) {
      groups.push({ label: `${lineItemLabel} (NDIS Price Catalogue)`, options: ndis });
    }
    if (custom.length) {
      if (shouldUseShiftFundingTypeSetting) {
        Object.entries(FUNDING_TYPE_LABELS).forEach(([fundingType, label]) => {
          const options = custom.filter((item) => item.funding_type === fundingType);
          if (options.length) {
            groups.push({ label: `Custom Line Items - ${label}`, options });
          }
        });
        const ungrouped = custom.filter(
          (item) => !item.funding_type || !FUNDING_TYPE_LABELS[item.funding_type]
        );
        if (ungrouped.length) {
          groups.push({
            label: "Previously selected line items",
            options: ungrouped,
          });
        }
      } else {
        groups.push({ label: "Custom Line Items", options: custom });
      }
    }
    return groups;
  }, [
    NDISItemNameOptionsForSelect,
    customLineItemOptions,
    lineItemLabel,
    shouldUseShiftFundingTypeSetting,
    shiftScreenFundingTypes,
    savedCustomLineItemUuid,
    savedInvoiceItemId,
    splitShift?.custom_line_item,
    splitShift?.support_item_name,
    splitShift?.support_item_number,
    splitShift?.price,
    splitInvoiceServiceTypeId,
    showClaimTypeForSplits,
    claimTypeForSplit,
  ]);
  const flatLineItemOptions = useMemo(
    () => groupedLineItemOptions.flatMap((group) => group.options || []),
    [groupedLineItemOptions]
  );
  const selectedNDISItemName = useMemo(() => {
    const hasCategoryCatalogue = flatLineItemOptions.some(
      (item) => !item.__orphan
    );
    if (splitShift?.custom_line_item_uuid) {
      const fromOptions = flatLineItemOptions.find(
        (item) =>
          item.__type === "custom" &&
          String(item.uuid) === String(splitShift.custom_line_item_uuid)
      );
      if (fromOptions) return fromOptions;
      // Aged Care / Private / Other: do not keep another category's custom
      // line selected when this category already has its own options.
      if (!hasCategoryCatalogue && !customLoading && !ndisLoading) {
        const orphan = buildOrphanCustomLineItemOption();
        if (
          orphan &&
          (orphan.invoice_service_type_id == null ||
            String(orphan.invoice_service_type_id) ===
              String(splitInvoiceServiceTypeId))
        ) {
          return orphan;
        }
      }
      return null;
    }
    const result =
      flatLineItemOptions.find(
        (item) =>
          item.__type === "ndis" &&
          String(item.value) === String(splitShift?.invoice_item_id)
      ) || null;
    if (result) return result;
    // Only keep a saved orphan when this category has no catalogue rows.
    if (!hasCategoryCatalogue && !ndisLoading && !customLoading) {
      return buildOrphanNdisLineItemOption();
    }
    return null;
  }, [
    NDISItemNameOptions,
    ndisLoading,
    customLoading,
    splitInvoiceServiceTypeId,
    splitShift?.invoice_item_id,
    splitShift?.custom_line_item_uuid,
    splitShift?.custom_line_item,
    splitShift?.support_item_name,
    splitShift?.support_item_number,
    splitShift?.price,
    flatLineItemOptions,
  ]);

  /** When travel is on and claim-type mapping is off: append ` - Travel` on `label` only. */
  const withTravelLabel = (opt) => {
    const raw = String(opt.name ?? opt.label ?? "").replace(
      /\s*-?\s*travel\s*$/i,
      ""
    );
    const base = capitalizeFirstLetter(raw.trim());
    return { ...opt, label: `${base} - Travel` };
  };

  const groupedLineItemOptionsForSelect = useMemo(() => {
    // Claim-type mapping already suffixes NDIS labels; avoid double-applying Travel.
    if (
      !showTravelForSplits ||
      !isTravelForSplit ||
      showClaimTypeForSplits
    ) {
      return groupedLineItemOptions;
    }
    return groupedLineItemOptions.map((group) => ({
      ...group,
      options: (group.options || []).map(withTravelLabel),
    }));
  }, [
    groupedLineItemOptions,
    showTravelForSplits,
    isTravelForSplit,
    showClaimTypeForSplits,
  ]);

  const selectedLineItemForSelect = useMemo(() => {
    if (!selectedNDISItemName) return null;
    // Ensure orphan / selected custom rows also show claim-type suffix.
    if (showClaimTypeForSplits && claimTypeForSplit) {
      return mapNdisOptionsForClaimType(
        [selectedNDISItemName],
        claimTypeForSplit
      )[0];
    }
    if (!showTravelForSplits || !isTravelForSplit) {
      return selectedNDISItemName;
    }
    return withTravelLabel(selectedNDISItemName);
  }, [
    selectedNDISItemName,
    showTravelForSplits,
    isTravelForSplit,
    showClaimTypeForSplits,
    claimTypeForSplit,
  ]);

  const applyLineItemToSplit = (option) => {
    if (!option) return;
    if (option.__type === "custom") {
      setValue(`splitShifts.${index}.custom_line_item_uuid`, option.uuid);
      setValue(`splitShifts.${index}.invoice_item_id`, "");
    } else {
      setValue(`splitShifts.${index}.invoice_item_id`, option.value);
      setValue(`splitShifts.${index}.custom_line_item_uuid`, null);
    }
    setValue(`splitShifts.${index}.support_item_number`, option.number || "");
    setValue(`splitShifts.${index}.support_item_name`, option.name || "");
    setValue(`splitShifts.${index}.price`, option.price ?? "");
    setValue(
      `splitShifts.${index}.duration`,
      getSegmentDurationValue(splitShift.start_time, splitShift.end_time) || 0
    );
  };

  useEffect(() => {
    const serviceTypeId = selectedServiceCategory?.value;
    if (
      !shiftDate ||
      !participantId ||
      !serviceTypeId ||
      !isNdisFundingType
    ) {
      setNDISItemName([]);
      setNdisLoading(false);
      return;
    }

    const formattedDate = moment.utc(shiftDate, "YYYY-MM-DD").toISOString();
    const url = buildShiftNdisItemsUrl({
      serviceTypeId,
      participantId,
      formattedShiftDate: formattedDate,
    });

    let cancelled = false;
    // Drop previous category's catalogue immediately so Self-Care etc. cannot
    // paint under Assistive Technology / other service categories.
    setNDISItemName([]);
    setNdisLoading(true);
    fetchShiftNdisItems(url)
      .then((items) => {
        if (!cancelled) {
          setNDISItemName(mapShiftNdisItemsToOptions(items));
        }
      })
      .catch(() => {
        if (!cancelled) setNDISItemName([]);
      })
      .finally(() => {
        if (!cancelled) setNdisLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    shiftDate,
    participantId,
    selectedServiceCategory?.value,
    isNdisFundingType,
  ]);
  const getSegmentDurationValue = (start, end) => {
    if (useNativeTimeInput) {
      const minutes = getBasicShiftWindowMinutes(start, end);
      if (minutes == null) return 0;
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
    }
    return getShiftLength(start, end, true) || 0;
  };
  const watchedStartTime = watch("start_time");
  const watchedEndTime = watch("end_time");
  const prevServiceCategoryRef = React.useRef();
  const isFirstRenderRef = React.useRef(true);
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
    } else if (
      prevServiceCategoryRef.current !== undefined &&
      prevServiceCategoryRef.current !== splitInvoiceServiceTypeId
    ) {
      setCustomLineItemOptions([]);
      setNDISItemName([]);
      setValue(`splitShifts.${index}.invoice_item_id`, "");
      setValue(`splitShifts.${index}.support_item_number`, "");
      setValue(`splitShifts.${index}.support_item_name`, "");
      setValue(`splitShifts.${index}.price`, "");
      setValue(`splitShifts.${index}.custom_line_item_uuid`, null);
      setValue(
        `splitShifts.${index}.claim_type`,
        showClaimTypeForSplits ? DEFAULT_ADVANCE_CLAIM_TYPE : null
      );
    }
    prevServiceCategoryRef.current = splitInvoiceServiceTypeId;
  }, [splitInvoiceServiceTypeId, index, setValue, showClaimTypeForSplits]);

  useEffect(() => {
    if (!selectedServiceCategory?.value) {
      return;
    }
    // Wait for NDIS and/or custom catalogues so Aged Care / Private / Other
    // do not keep another category's line item as the default selection.
    if (isNdisFundingType && ndisLoading) {
      return;
    }
    if (customLoading) {
      return;
    }

    // Only real catalogue rows for the current category (never orphans).
    const categoryOptions = flatLineItemOptions.filter(
      (item) => !item.__orphan
    );
    if (!categoryOptions.length) {
      return;
    }

    const selectedInCurrentCategory = categoryOptions.some((item) => {
      if (splitShift?.custom_line_item_uuid) {
        return (
          item.__type === "custom" &&
          String(item.uuid) === String(splitShift.custom_line_item_uuid) &&
          (item.invoice_service_type_id == null ||
            String(item.invoice_service_type_id) ===
              String(splitInvoiceServiceTypeId))
        );
      }
      if (splitShift?.invoice_item_id != null && splitShift?.invoice_item_id !== "") {
        return (
          item.__type === "ndis" &&
          String(item.value) === String(splitShift.invoice_item_id)
        );
      }
      return false;
    });

    if (selectedInCurrentCategory) {
      return;
    }

    // Clear cross-category leftovers, then default to first item for THIS category.
    if (
      splitShift?.custom_line_item_uuid ||
      (splitShift?.invoice_item_id != null && splitShift?.invoice_item_id !== "") ||
      splitShift?.support_item_number
    ) {
      setValue(`splitShifts.${index}.invoice_item_id`, "");
      setValue(`splitShifts.${index}.custom_line_item_uuid`, null);
      setValue(`splitShifts.${index}.support_item_number`, "");
      setValue(`splitShifts.${index}.support_item_name`, "");
      setValue(`splitShifts.${index}.price`, "");
    }

    applyLineItemToSplit(categoryOptions[0]);
  }, [
    selectedServiceCategory?.value,
    splitInvoiceServiceTypeId,
    flatLineItemOptions,
    ndisLoading,
    customLoading,
    isNdisFundingType,
    splitShift?.custom_line_item_uuid,
    splitShift?.invoice_item_id,
    splitShift?.support_item_number,
    splitShift?.start_time,
    splitShift?.end_time,
    index,
    setValue,
  ]);
  useEffect(() => {
    if (index === 0 && watchedStartTime) {
      setValue("splitShifts.0.start_time", watchedStartTime);
    }
    if (index === allSplitShifts.length - 1 && watchedEndTime) {
      setValue(`splitShifts.${index}.end_time`, watchedEndTime);
    }
  }, [index, setValue, watchedStartTime, watchedEndTime, allSplitShifts.length]);
  const calculateTotalBreakdownTime = () => {
    let totalMinutes = 0;
    const splits = watch("splitShifts") || [];
    for (let i = 0; i < splits.length; i++) {
      const { start_time, end_time } = splits[i];
      if (!start_time || !end_time) continue;

      if (useNativeTimeInput) {
        const segmentMinutes = getBasicShiftWindowMinutes(start_time, end_time);
        if (segmentMinutes != null) totalMinutes += segmentMinutes;
        continue;
      }

      const startMoment = moment(start_time, ["HH:mm", "hh:mm A", "h:mm A"], true);
      let endMoment = moment(end_time, ["HH:mm", "hh:mm A", "h:mm A"], true);
      if (startMoment.isValid() && endMoment.isValid()) {
        if (endMoment.isBefore(startMoment)) {
          endMoment = endMoment.add(1, "days");
        }
        totalMinutes += moment.duration(endMoment.diff(startMoment)).asMinutes();
      }
    }
    return totalMinutes;
  };
  const validateTimeRange = () => {
    const totalShiftMinutes = calculateTotalBreakdownTime();
    const totalDurationMinutes = moment.duration(totalShiftDuration).asMinutes();
    if (totalShiftMinutes !== totalDurationMinutes) {
      return `The total duration of all shift splits combined must be exactly ${
        totalShiftDuration?.split(":")[0]
      } hours and ${totalShiftDuration?.split(":")[1]} minutes in total.`;
    }
    return true;
  };
  useEffect(() => {
    if (!NDISItemNameOptions && !flatLineItemOptions.length) return;
    let found = null;
    if (splitShift?.custom_line_item_uuid) {
      found = flatLineItemOptions.find(
        (item) => item.__type === "custom" && item.uuid === splitShift.custom_line_item_uuid
      );
      if (found) {
        setValue(`splitShifts.${index}.custom_line_item_uuid`, found.uuid);
        setValue(`splitShifts.${index}.support_item_number`, found.number || "");
        setValue(`splitShifts.${index}.support_item_name`, found.name || "");
        setValue(`splitShifts.${index}.price`, found.price ?? "");
      }
    } else if (splitShift?.invoice_item_id) {
      found = (NDISItemNameOptions || []).find(
        (item) => String(item.value) === String(splitShift.invoice_item_id)
      );
      if (found) setValue(`splitShifts.${index}.invoice_item_id`, found.value);
    } else if (splitShift?.support_item_number) {
      found = (NDISItemNameOptions || []).find(
        (item) => item.number === splitShift.support_item_number
      );
      if (found) setValue(`splitShifts.${index}.invoice_item_id`, found.value);
    }
    if (found && found.__type !== "custom") {
      setValue(`splitShifts.${index}.support_item_number`, found.number || "");
      setValue(`splitShifts.${index}.support_item_name`, found.name || "");
      setValue(`splitShifts.${index}.price`, found.price || "");
      setValue(
        `splitShifts.${index}.duration`,
        getSegmentDurationValue(splitShift.start_time, splitShift.end_time) || 0
      );
    }
  }, [
    NDISItemNameOptions,
    flatLineItemOptions,
    splitShift?.invoice_item_id,
    splitShift?.support_item_number,
    splitShift?.custom_line_item_uuid,
    splitShift?.start_time,
    splitShift?.end_time,
    index,
    setValue,
  ]);
  useEffect(() => {
    if (!selectedNDISItemName) return;
    if (selectedNDISItemName.__type === "custom") {
      setValue(`splitShifts.${index}.custom_line_item_uuid`, selectedNDISItemName.uuid);
      setValue(`splitShifts.${index}.invoice_item_id`, "");
    } else {
      setValue(`splitShifts.${index}.invoice_item_id`, selectedNDISItemName.value);
      setValue(`splitShifts.${index}.custom_line_item_uuid`, null);
    }
    setValue(`splitShifts.${index}.support_item_number`, selectedNDISItemName.number || "");
    setValue(`splitShifts.${index}.support_item_name`, selectedNDISItemName.name || "");
    setValue(`splitShifts.${index}.price`, selectedNDISItemName.price ?? "");
    setValue(
      `splitShifts.${index}.duration`,
      getSegmentDurationValue(splitShift.start_time, splitShift.end_time) || 0
    );
  }, [selectedNDISItemName, index, setValue, splitShift?.start_time, splitShift?.end_time]);
  useEffect(() => {
    if (
      splitShift?.start_time &&
      splitShift?.end_time &&
      (splitShift?.start_time !== splitShift?.end_time ||
        isBasicSameClockTimeFullDay(splitShift?.start_time, splitShift?.end_time))
    ) {
      clearErrors(`splitShifts.${index}.end_time`);
    }
  }, [splitShift?.start_time, splitShift?.end_time, clearErrors, index]);
  useEffect(() => {
    const sleepoverSelected = allSplitShifts?.some((split) => split?.sleepover === true);
    if (!watchedStartTime || !watchedEndTime || !sleepoverSelected) {
      setIsLessThan12Hours(false);
      return;
    }
    const parseFormats = ["HH:mm", "hh:mm A", "h:mm A"];
    const start = moment(watchedStartTime, parseFormats, true);
    const end = moment(watchedEndTime, parseFormats, true);
    if (!start.isValid() || !end.isValid()) {
      setIsLessThan12Hours(false);
      return;
    }
    if (end.isBefore(start)) {
      end.add(1, "day");
    } else if (isBasicSameClockTimeFullDay(watchedStartTime, watchedEndTime)) {
      end.add(1, "day");
    }
    setIsLessThan12Hours(end.diff(start, "hours", true) < 12);
  }, [watchedStartTime, watchedEndTime, splitShift?.sleepover, allSplitShifts, setIsLessThan12Hours]);
  return (
    <div className='bg-white rounded-xl shadow-sm border border-gray-300 p-4 mb-3 relative transition-all duration-200 hover:shadow-md'>
      <button
        type='button'
        onClick={onRemove}
        className='absolute -top-3 -right-3 bg-white text-red-400 p-1.5 rounded-full shadow-md hover:bg-red-50 hover:text-red-600 transition-all duration-200 border border-gray-200'
        aria-label='Remove shift'
      >
        <CgRemoveR size={18} />
      </button>
      <div className='flex items-center mb-1'>
        <span className='w-1 h-6 bg-blue-500 rounded-full mr-1'></span>
        <h3 className='text-sm font-semibold text-gray-800'>
          Split Shift.# {index + 1}
        </h3>
        {nextDayStartIndex !== -1 && index >= nextDayStartIndex && (
          <div className='ml-1'>
            <span className='text-xs border border-orange-600 text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full font-semibold'>
              Goes to next date ({moment(watch("shift_date"), "YYYY-MM-DD").add(1, "days").format("DD/MM/YYYY")})
            </span>
          </div>
        )}
      </div>
      <div className='grid grid-cols-1 md:grid-cols-3 gap-5 mb-2'>
        <div>
          {useNativeTimeInput ? (
            <>
              <AdvanceModeFieldLabel>
                Start time
                {isFirstSplitShift && (
                  <span className='ml-1.5 normal-case font-medium text-slate-400'>
                    (linked)
                  </span>
                )}
              </AdvanceModeFieldLabel>
              <input
                type='time'
                className={ADVANCE_TIME_INPUT_CLASS}
                disabled={isFirstSplitShift}
                {...(() => {
                  const { ref, ...rest } = register(`splitShifts.${index}.start_time`, {
                    required: "Start time is required",
                    validate: () => (!isFirstSplitShift ? validateTimeRange() : true),
                  });
                  return rest;
                })()}
                value={toTimeInputValue(splitShift?.start_time)}
                onChange={(e) =>
                  setValue(`splitShifts.${index}.start_time`, e.target.value, {
                    shouldDirty: true,
                  })
                }
              />
            </>
          ) : (
            <>
              <label className='text-gray-700 text-sm font-medium block'>
                Start Time
                {isFirstSplitShift && (
                  <span className='ml-1 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full'>
                    Not Changeable
                  </span>
                )}
              </label>
              <div
                className='w-full'
                style={{
                  pointerEvents: isFirstSplitShift ? "none" : "auto",
                  opacity: isFirstSplitShift ? 0.6 : 1,
                }}
              >
                <TimePicker
                  className='did-floating-input bg-white text-gray-900 rounded px-2 py-1 w-full sm:w-auto text-gray-900'
                  {...(() => {
                    const { ref: _startTimeFieldRef, ...rest } = register(
                      `splitShifts.${index}.start_time`,
                      {
                        required: "Start time is required",
                        validate: () => (!isFirstSplitShift ? validateTimeRange() : true),
                      }
                    );
                    return rest;
                  })()}
                  value={splitShift?.start_time || ""}
                  onChange={(value) => setValue(`splitShifts.${index}.start_time`, value)}
                  hour12Format={true}
                  eachInputDropdown={true}
                  manuallyDisplayDropdown={true}
                />
              </div>
            </>
          )}
        </div>
        <div>
          {useNativeTimeInput ? (
            <>
              <AdvanceModeFieldLabel>
                End time
                {isLastSplitShift && (
                  <span className='ml-1.5 normal-case font-medium text-slate-400'>
                    (linked)
                  </span>
                )}
              </AdvanceModeFieldLabel>
              <input
                type='time'
                className={ADVANCE_TIME_INPUT_CLASS}
                disabled={isLastSplitShift}
                {...(() => {
                  const { ref, ...rest } = register(`splitShifts.${index}.end_time`, {
                    required: "End time is required",
                    validate: (value) => {
                      const start = splitShift?.start_time;
                      if (useNativeTimeInput) {
                        const splitCheck = validateBasicSplitEndTime(start, value);
                        if (splitCheck !== true) return splitCheck;
                      } else if (value === start) {
                        return "start time and end time can't be same!";
                      }
                      const shiftLengthValidation = getShiftLength(
                        start,
                        value,
                        false,
                        true
                      );
                      if (shiftLengthValidation === "00:00") {
                        return "Shift can't be zero hours & minutes!";
                      }
                      return !isLastSplitShift ? validateTimeRange() : true;
                    },
                  });
                  return rest;
                })()}
                value={toTimeInputValue(splitShift?.end_time)}
                onChange={(e) => {
                  const value = e.target.value;
                  setValue(`splitShifts.${index}.end_time`, value, { shouldDirty: true });
                  if (index < allSplitShifts.length - 1) {
                    setValue(`splitShifts.${index + 1}.start_time`, value, {
                      shouldDirty: true,
                    });
                  }
                }}
              />
              {errors?.splitShifts?.[index]?.end_time && (
                <AdvanceModeFieldError
                  message={errors?.splitShifts[index]?.end_time?.message}
                />
              )}
            </>
          ) : (
            <>
              <label className='text-gray-700 text-sm font-medium block'>
                End Time
                {isLastSplitShift && (
                  <span className='ml-1 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full'>
                    Not Changeable
                  </span>
                )}
              </label>
              <div
                className='w-full'
                style={{
                  pointerEvents: isLastSplitShift ? "none" : "auto",
                  opacity: isLastSplitShift ? 0.6 : 1,
                }}
              >
                <TimePicker
                  hour12Format={true}
                  eachInputDropdown={true}
                  manuallyDisplayDropdown={true}
                  className='did-floating-input bg-white text-gray-900 rounded px-2 py-1 w-full sm:w-auto text-gray-900'
                  {...(() => {
                    const { ref: _endTimeFieldRef, ...rest } = register(
                      `splitShifts.${index}.end_time`,
                      {
                        required: "End time is required",
                        validate: (value) => {
                          if (value === splitShift?.start_time) {
                            return "start time and end time can't be same!";
                          }
                          const shiftLengthValidation = getShiftLength(
                            splitShift?.start_time,
                            value,
                            false,
                            true
                          );
                          if (shiftLengthValidation === "00:00") {
                            return "Shift can't be zero hours & minutes!";
                          }
                          return !isLastSplitShift ? validateTimeRange() : true;
                        },
                      }
                    );
                    return rest;
                  })()}
                  value={splitShift?.end_time || ""}
                  onChange={(value) => {
                    setValue(`splitShifts.${index}.end_time`, value);
                    if (index < allSplitShifts.length - 1) {
                      setValue(`splitShifts.${index + 1}.start_time`, value);
                    }
                  }}
                />
                {errors?.splitShifts?.[index]?.end_time && (
                  <span className='text-red-600 text-sm'>
                    {errors?.splitShifts[index]?.end_time?.message}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
        <div>
          <label className='text-gray-700 text-sm font-medium block'>
            Service Category
          </label>
          <SelectOption
            data={groupedServiceTypes}
            isRequired={true}
            register={register}
            setValue={setValue}
            clearErrors={clearErrors}
            name={`splitShifts.${index}.invoice_service_type_id`}
            cssClass='z-20'
            selectedOption={selectedServiceCategory}
            useMenuPortal={true}
            detachRegisterRef={true}
            handleChange={(option) => {
              const nextId = option?.value ?? null;
              setValue(`splitShifts.${index}.invoice_service_type_id`, nextId, {
                shouldValidate: showServiceCategoryError,
                shouldDirty: true,
              });
              if (nextId != null && nextId !== "") {
                clearErrors?.(
                  `splitShifts.${index}.invoice_service_type_id`
                );
              }
            }}
            errorMessage={
              showServiceCategoryError &&
              (errors?.splitShifts?.[index]?.invoice_service_type_id?.type ===
                "required" ||
                errors?.splitShifts?.[index]?.invoice_service_type_id?.message)
                ? errors?.splitShifts?.[index]?.invoice_service_type_id
                    ?.message || "This field is required!"
                : null
            }
          />
        </div>
      </div>
      <div className='relative my-2'>
        <div className='absolute inset-0 flex items-center' aria-hidden='true'>
          <div className='w-full border-t border-gray-200'></div>
        </div>
        <div className='relative flex justify-center'>
          <span className='px-3 bg-white text-sm text-gray-400'>*****</span>
        </div>
      </div>
      <div className='grid grid-cols-1 md:grid-cols-3 gap-5'>
        <div>
          <label className='text-gray-700 text-sm font-medium block'>
            {lineItemLabel} / Custom Line
            {splitShift?.custom_line_item?.status === "inactive" && (
              <span className='ml-1.5 inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800'>
                Inactive
              </span>
            )}
          </label>
          <SelectOption
            key={`splitShifts.${index}.invoice_item_id-${claimTypeForSplit ?? "none"}`}
            className='w-full'
            data={groupedLineItemOptionsForSelect}
            isRequired={false}
            register={register}
            setValue={setValue}
            clearErrors={clearErrors}
            name={`splitShifts.${index}.invoice_item_id`}
            cssClass='z-20'
            selectedOption={selectedLineItemForSelect}
            handleChange={(option) => {
              if (!option) {
                setValue(`splitShifts.${index}.invoice_item_id`, "");
                setValue(`splitShifts.${index}.custom_line_item_uuid`, null);
                setValue(`splitShifts.${index}.support_item_number`, "");
                setValue(`splitShifts.${index}.support_item_name`, "");
                setValue(`splitShifts.${index}.price`, "");
                return;
              }
              applyLineItemToSplit(option);
            }}
            isDisabled={ndisLoading || customLoading}
            loading={ndisLoading || customLoading}
            useMenuPortal={true}
            errorMessage={
              errors?.splitShifts?.[index]?.invoice_item_id?.type === "required"
                ? "This field is required!"
                : null
            }
          />
        </div>
        <div>
          <label className='text-gray-700 text-sm font-medium block'>
            {lineItemLabel} Number
          </label>
          <InputField
            readOnly={true}
            cssClass='mt-0 h-40-important text-gray-700'
            inputWidth='w-full'
            type='text'
            name={`splitShifts.${index}.support_item_number`}
            isRequired={true}
            register={register}
            setValue={setValue}
            backgroundColor='#F3F4F6'
          />
        </div>
        {showClaimTypeForSplits && (
          <div className='min-w-0'>
            <label className='text-gray-700 text-sm font-medium block mb-1'>
              Claim type
            </label>
            <SelectOption
              data={ADVANCE_CLAIM_TYPE_OPTIONS}
              isClearable={true}
              register={register}
              setValue={setValue}
              clearErrors={clearErrors}
              name={`splitShifts.${index}.claim_type`}
              cssClass='z-20'
              selectedOption={selectedClaimType}
              handleChange={(option) => {
                setValue(
                  `splitShifts.${index}.claim_type`,
                  option?.value ?? null
                );
              }}
              useMenuPortal={true}
            />
          </div>
        )}
        {(selectedServiceCategory?.label === "Self-Care (Deprecated)" ||
          selectedServiceCategory?.label === "Home and Living" ||
          selectedServiceCategory?.label === "Assistance with Daily Life") && (
          <div
            className='flex items-center gap-x-2 shrink-0 col-span-1 md:col-span-3'
            title='select this option if sleepover shift'
          >
            <label className='text-gray-700 text-sm font-medium mb-1'>
              Sleepover Shift
            </label>
            <div>
              <Controller
                name={`splitShifts.${index}.sleepover`}
                control={control}
                defaultValue={splitShift?.sleepover || false}
                render={({ field }) => (
                  <SwitchWithIcon
                    onChange={(value) => {
                      field.onChange(value);
                      setValue(`splitShifts.${index}.sleepover`, value);
                      onSplitSleepoverChange?.(index, value);
                    }}
                    checked={parseBooleanVal(field.value)}
                  />
                )}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default SplitShift;
